import React, { useRef, useMemo } from "react";
import { scaleLinear } from "d3-scale";
import { extent } from "d3-array";
import { useChartDimensions } from "./common/utils";

const MARGIN = { top: 30, right: 30, bottom: 55, left: 70 };
const ACCEPTED_COLOR = "rgb(0,0,255)";
const EXCLUDED_COLOR = "rgb(255,0,0)";

const TempCorrGraph = ({ graphData }) => {
    const ref = useRef(null);
    const { width, height } = useChartDimensions(ref);

    const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
    const innerHeight = Math.max(0, (height || 450) - MARGIN.top - MARGIN.bottom);

    const { xScale, yScale } = useMemo(() => {
        if (!graphData?.length) return {};
        const xScale = scaleLinear()
            .domain(extent(graphData, d => d.timestamps))
            .range([0, innerWidth]);
        const yScale = scaleLinear()
            .domain(extent(graphData, d => d.qualities))
            .nice()
            .range([innerHeight, 0]);
        return { xScale, yScale };
    }, [graphData, innerWidth, innerHeight]);

    if (!graphData?.length || !xScale) {
        return <div ref={ref} style={{ height: 450 }}><p>No data</p></div>;
    }

    const xTicks = xScale.ticks(6);
    const yTicks = yScale.ticks(5);

    return (
        <div ref={ref} style={{ height: 450 }} className="graphborder graphborder-dashed border-cyan-500">
            <svg width={width} height={height || 450}>
                <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
                    {/* Grid lines */}
                    {yTicks.map(t => (
                        <line key={t} x1={0} x2={innerWidth} y1={yScale(t)} y2={yScale(t)}
                            stroke="#e5e7eb" strokeWidth={1} />
                    ))}

                    {/* Data points */}
                    {graphData.map((d, i) => {
                        const cx = xScale(d.timestamps);
                        const cy = yScale(d.qualities);
                        if (!cx || !cy) return null;
                        return (
                            <circle key={i} cx={cx} cy={cy} r={2}
                                fill={d.qc === "Accepted" ? ACCEPTED_COLOR : EXCLUDED_COLOR}
                                fillOpacity={0.4} />
                        );
                    })}

                    {/* X axis */}
                    <g transform={`translate(0,${innerHeight})`}>
                        <line x1={0} x2={innerWidth} stroke="#374151" />
                        {xTicks.map(t => (
                            <g key={t} transform={`translate(${xScale(t)},0)`}>
                                <line y2={5} stroke="#374151" />
                                <text y={18} textAnchor="middle" fontSize={10}>
                                    {(t / 1e6).toFixed(1)}s
                                </text>
                            </g>
                        ))}
                        <text x={innerWidth / 2} y={42} textAnchor="middle" fontSize={11} fill="#374151">
                            Time (s)
                        </text>
                    </g>

                    {/* Y axis */}
                    <g>
                        <line y1={0} y2={innerHeight} stroke="#374151" />
                        {yTicks.map(t => (
                            <g key={t} transform={`translate(0,${yScale(t)})`}>
                                <line x1={-5} stroke="#374151" />
                                <text x={-8} dy="0.32em" textAnchor="end" fontSize={10}>
                                    {t.toFixed(2)}
                                </text>
                            </g>
                        ))}
                        <text transform={`translate(-52,${innerHeight / 2}) rotate(-90)`}
                            textAnchor="middle" fontSize={11} fill="#374151">
                            Normalised Correlation
                        </text>
                    </g>

                    {/* Legend */}
                    <g transform={`translate(${innerWidth - 110}, 0)`}>
                        <circle cx={9} cy={6} r={4} fill={ACCEPTED_COLOR} fillOpacity={0.7} />
                        <text x={18} y={10} fontSize={10}>Accepted</text>
                        <circle cx={9} cy={22} r={4} fill={EXCLUDED_COLOR} fillOpacity={0.7} />
                        <text x={18} y={26} fontSize={10}>Excluded</text>
                    </g>
                </g>
            </svg>
        </div>
    );
};

export default TempCorrGraph;
